import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import {
  uploadBillSupportingDocuments,
  deleteBillSupportingDocument,
} from "api";
import { useNotify } from "hooks";

import { QUERY_KEYS } from "consts";

export const useUploadBillSupportingDocument = () => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  return useMutation(
    ({ guid, file }: { guid: string; file: File }) =>
      uploadBillSupportingDocuments([{ guid, fileIndices: [0] }], [file]),
    {
      onSuccess: () => {
        notify.success("Document uploaded successfully!");
        queryClient.invalidateQueries([QUERY_KEYS.DOCUMENT_BILLS]);
        queryClient.invalidateQueries([QUERY_KEYS.UTILITY_BILLS]);
      },
    },
  );
};

export const useUploadBillSupportingDocumentsBulk = () => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  return useMutation(
    ({ billIds, file }: { billIds: string[]; file: File }) =>
      uploadBillSupportingDocuments(
        billIds.map((guid) => ({ guid, fileIndices: [0] })),
        [file],
      ),
    {
      onSuccess: (_, { billIds }) => {
        notify.success(`Document attached to ${billIds.length} ${billIds.length === 1 ? "entry" : "entries"}.`);
        queryClient.invalidateQueries([QUERY_KEYS.DOCUMENT_BILLS]);
        queryClient.invalidateQueries([QUERY_KEYS.UTILITY_BILLS]);
      },
    },
  );
};

export const useDeleteBillSupportingDocument = () => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  return useMutation(
    (id: number) => deleteBillSupportingDocument(id),
    {
      onSuccess: () => {
        notify.success("Document deleted successfully!");
        queryClient.invalidateQueries([QUERY_KEYS.DOCUMENT_BILLS]);
        queryClient.invalidateQueries([QUERY_KEYS.UTILITY_BILLS]);
      },
      onError: (error: AxiosError) => {
        notify.error(
          typeof error?.response?.data === "string"
            ? error.response.data
            : "Failed to delete document",
        );
      },
    },
  );
};
